import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import RouteBuilder from './components/RouteBuilder';
import RouteDisplay from './components/RouteDisplay';
import WineryDetail from './components/WineryDetail';
import WinePreferencesModal from './components/WinePreferencesModal';
import RegionSelector from './components/RegionSelector';
import WinerySearch from './components/WinerySearch';
import SafetyBanner from './components/SafetyBanner';
import FerryIslandView from './components/FerryIslandView';

import { getWineRoute, clearVisitedWineries, searchWineries, checkSafety } from './lib/api';

const CACHED_ROUTE_KEY = 'cowichan_cached_route';

const VIEW_STATES = {
  HOME: 'home',
  BUILDING: 'building',
  ROUTE: 'route',
  FERRY: 'ferry',
};

const FERRY_ISLAND_REGIONS = new Set([
  'salt-spring-island',
  'pender-island',
  'saturna-island',
  'denman-island',
  'hornby-island',
]);

export default function App() {
  const [view, setView] = useState(VIEW_STATES.HOME);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isCachedRoute, setIsCachedRoute] = useState(false);
  const [arrivedViaShare, setArrivedViaShare] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const [currentRoute, setCurrentRoute] = useState(null);
  const [selectedWinery, setSelectedWinery] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  const [selectedRegion, setSelectedRegion] = useState('central-cowichan');
  const [safetyAlerts, setSafetyAlerts] = useState([]);
  const [safetySeverity, setSafetySeverity] = useState('none');
  const peakSeverityRef = useRef('none');

  const [winePreferences, setWinePreferences] = useState({
    wineType: 'any',
    selectedWines: [],
    amenities: []
  });

  // On load: shared route from URL ?stops= takes priority, then sessionStorage, then localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stopsParam = params.get('stops');

    if (stopsParam) {
      window.history.replaceState({}, '', window.location.pathname);
      setArrivedViaShare(true);
      const stopIds = stopsParam.split(',').filter(Boolean);
      if (stopIds.length > 0) {
        setIsLoading(true);
        searchWineries('').then(allWineries => {
          const stops = stopIds
            .map((id, index) => {
              const w = allWineries.find(w => w.id === id);
              return w ? { ...w, stop_number: index + 1 } : null;
            })
            .filter(Boolean);
          if (stops.length > 0) {
            setCurrentRoute({
              route_name: 'Shared Wine Route',
              total_stops: stops.length,
              estimated_time: null,
              stops,
            });
            setView(VIEW_STATES.ROUTE);
          }
        }).catch(() => {}).finally(() => setIsLoading(false));
      }
      return;
    }

    const sessionSaved = sessionStorage.getItem('cowichan_wine_route');
    if (sessionSaved) {
      try {
        setCurrentRoute(JSON.parse(sessionSaved));
        setView(VIEW_STATES.ROUTE);
        return;
      } catch {}
    }

    try {
      const localSaved = localStorage.getItem(CACHED_ROUTE_KEY);
      if (localSaved) {
        const { route } = JSON.parse(localSaved);
        setCurrentRoute(route);
        setIsCachedRoute(true);
        setView(VIEW_STATES.ROUTE);
      }
    } catch {}
  }, []);

  // Safety monitoring — runs on load and every 30 minutes
  useEffect(() => {
    const testLevel = new URLSearchParams(window.location.search).get('test-alert');
    if (testLevel) {
      const MOCK = {
        advisory:      { severity: 'advisory',  alerts: [{ type: 'wildfire',     severity: 'advisory',  banner: 'Burnt Bridge Fire — 34 km northeast, being held', title: 'Burnt Bridge Fire', distance: '34 km northeast of your location', size: '142 ha', status: 'Being Held', windTowardUser: false, windSpeed: 12, url: 'https://wildfiresituation.nrs.gov.bc.ca' }] },
        warning:       { severity: 'warning',   alerts: [{ type: 'wildfire',     severity: 'warning',   banner: 'Shawnigan Creek Fire — 18 km northwest, out of control', title: 'Shawnigan Creek Fire', distance: '18 km northwest of your location', size: '890 ha', status: 'Out of Control', windTowardUser: true, windSpeed: 28, url: 'https://wildfiresituation.nrs.gov.bc.ca' }] },
        emergency:     { severity: 'emergency', alerts: [{ type: 'evacuation',   severity: 'emergency', banner: 'Evacuation Order — Shawnigan Lake West', title: 'Evacuation Order in Effect', distance: 'Your planned route or current location is in an affected area', status: 'Evacuation Order', url: 'https://www.emergencyinfobc.gov.bc.ca' }] },
        'air-quality': { severity: 'advisory',  alerts: [{ type: 'air-quality',  severity: 'advisory',  banner: 'Air quality unhealthy for sensitive groups along your route', title: 'Air Quality Advisory', distance: 'Air Quality Index: 118 — Unhealthy for Sensitive Groups. Seniors and those with breathing conditions should take precautions.', aqi: 118, url: 'https://www.env.gov.bc.ca/epd/bcairquality/readings/' }] },
        heat:          { severity: 'warning',   alerts: [{ type: 'heat',         severity: 'warning',   banner: 'Heat Warning along your route', title: 'Heat Warning', distance: 'Extreme heat expected. Risk of heat illness, especially for seniors and those with health conditions.', status: 'Heat Warning', url: 'https://weather.gc.ca/warnings/index_e.html?prov=bc' }] },
        thunderstorm:  { severity: 'advisory',  alerts: [{ type: 'thunderstorm', severity: 'advisory',  banner: 'Thunderstorm Watch — severe storms possible along your route', title: 'Thunderstorm Watch', distance: 'Conditions are favourable for the development of severe thunderstorms with heavy rain, hail, and strong winds.', status: 'Thunderstorm Watch', url: 'https://weather.gc.ca/warnings/index_e.html?prov=bc' }] },
        multi:         { severity: 'warning',   alerts: [
          { type: 'wildfire',    severity: 'warning',  banner: 'Shawnigan Creek Fire — 18 km northwest, out of control', title: 'Shawnigan Creek Fire', distance: '18 km northwest of your location', size: '890 ha', status: 'Out of Control', windTowardUser: true, windSpeed: 28, url: 'https://wildfiresituation.nrs.gov.bc.ca' },
          { type: 'air-quality', severity: 'advisory', banner: 'Air quality unhealthy for sensitive groups along your route', title: 'Air Quality Advisory', distance: 'Air Quality Index: 118 — Unhealthy for Sensitive Groups.', aqi: 118, url: 'https://www.env.gov.bc.ca/epd/bcairquality/readings/' },
        ]},
      };
      const mock = MOCK[testLevel] || MOCK.advisory;
      setSafetySeverity(mock.severity);
      setSafetyAlerts(mock.alerts);
      return;
    }

    const SEVERITY_ORDER = { emergency: 3, warning: 2, advisory: 1, none: 0 };

    function runSafetyCheck() {
      peakSeverityRef.current = 'none';
      const stops = (currentRoute?.stops || []).map(s => ({
        lat: s.coordinates?.lat ?? s.latitude,
        lng: s.coordinates?.lng ?? s.longitude,
        name: s.name,
      })).filter(s => s.lat && s.lng);

      const doCheck = (lat, lng) => {
        checkSafety(lat, lng, stops).then(result => {
          const newSeverity = result.severity || 'none';
          // Only update if the new result is equally or more severe — prevents
          // a fast GPS check from wiping out an alert already shown from route stops
          if ((SEVERITY_ORDER[newSeverity] ?? 0) >= (SEVERITY_ORDER[peakSeverityRef.current] ?? 0)) {
            peakSeverityRef.current = newSeverity;
            setSafetySeverity(newSeverity);
            setSafetyAlerts(result.alerts || []);
          }
        });
      };

      const fallback = stops[0];

      // Run immediately using route stop coordinates — no GPS wait
      if (fallback) doCheck(fallback.lat, fallback.lng);

      // Also run with GPS if available for more accurate user-position check
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => doCheck(pos.coords.latitude, pos.coords.longitude),
          () => {},
          { timeout: 10000, maximumAge: 300000 }
        );
      }
    }
    runSafetyCheck();
    const interval = setInterval(runSafetyCheck, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentRoute]);

  // Online/offline detection
  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Keep sessionStorage in sync
  useEffect(() => {
    if (currentRoute) {
      sessionStorage.setItem('cowichan_wine_route', JSON.stringify(currentRoute));
    }
  }, [currentRoute]);

  // Scroll to top on view change
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    requestAnimationFrame(() => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, 0);
    });
  }, [view]);

  const handleBuildRoute = async ({ duration, region, startLocation }) => {
    setIsLoading(true);
    setError(null);
    setUserLocation(startLocation || null);

    try {
      const source = arrivedViaShare ? 'shared_referral' : 'direct';
      const route = await getWineRoute(duration, region, startLocation, winePreferences, source);
      try {
        localStorage.setItem(CACHED_ROUTE_KEY, JSON.stringify({ route, savedAt: Date.now() }));
      } catch {}
      setIsCachedRoute(false);
      setCurrentRoute(route);
      setView(VIEW_STATES.ROUTE);
    } catch (err) {
      console.error('Failed to build route:', err);
      try {
        const localSaved = localStorage.getItem(CACHED_ROUTE_KEY);
        if (localSaved) {
          const { route } = JSON.parse(localSaved);
          setCurrentRoute(route);
          setIsCachedRoute(true);
          setView(VIEW_STATES.ROUTE);
          return;
        }
      } catch {}
      setError(err.message || 'Failed to build route. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentRoute(null);
    setSelectedWinery(null);
    setIsCachedRoute(false);
    setView(VIEW_STATES.HOME);
    sessionStorage.removeItem('cowichan_wine_route');
    localStorage.removeItem(CACHED_ROUTE_KEY);
  };

  const handleSelectWinery = (winery) => {
    if (winery.card) {
      setSelectedWinery({
        ...winery,
        revised_description: winery.card.description,
        hours_text: winery.card.hours,
        tasting_fee: winery.card.tastingFee,
        tasting_fee_updated_at: winery.card.tastingFeeUpdatedAt,
        reservation_required: winery.card.reservationRequired,
        features: winery.card.features,
        wines_offered: winery.card.wines,
        vineyard_experiences: winery.card.experiences,
        restaurant_open: winery.card.restaurantOpen,
        has_restaurant: winery.card.hasRestaurant,
        food_service: winery.card.foodService,
        image_url: winery.card.imageUrl,
        latitude: winery.coordinates?.lat,
        longitude: winery.coordinates?.lng,
      });
    } else {
      setSelectedWinery(winery);
    }
  };

  const handleCloseDetail = () => setSelectedWinery(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100">
      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Safety Banner — shown on all views when there is an active alert */}
        {safetySeverity !== 'none' && (
          <div className="mb-3">
            <SafetyBanner key={currentRoute?.stops?.[0]?.name} alerts={safetyAlerts} severity={safetySeverity} />
          </div>
        )}

        {/* Home View */}
        {view === VIEW_STATES.HOME && (
          <div className="space-y-2">
            {/* Hero */}
            <div className="relative text-center pt-0 pb-4 mb-2">
              <button
                onClick={() => setShowHelp(true)}
                className="absolute top-0 right-0 w-14 h-14 rounded-full bg-stone-200 hover:bg-stone-300 text-stone-600 text-base font-bold flex items-center justify-center transition-colors z-10"
                aria-label="How to use"
              >
                ?
              </button>
              <div style={{
                width: '308px',
                height: '155px',
                overflow: 'hidden',
                margin: '18px auto -63px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}>
                <img
                  src="/logo-transparent.png"
                  alt="Cowichan Wine Route"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'contain',
                    flexShrink: 0,
                    marginTop: '-108px',
                    transform: 'scaleX(1.15)',
                    transformOrigin: 'center top',
                  }}
                />
              </div>
              <h2 className="text-2xl font-bold text-stone-800 mb-2">
                Discover Vancouver Island Wines
              </h2>
              <p className="text-stone-500">
                From the Cowichan Valley to the Comox Valley and Gulf Islands — boutique estates, stunning scenery, and island hospitality.
              </p>
              <p className="mt-3 text-sm text-stone-600 flex items-center justify-center gap-2">
                <span className="flex items-center gap-1">
                  {['1','2','3'].map(n => (
                    <span key={n} className="w-5 h-5 rounded bg-[#722F37] text-white text-xs font-bold flex items-center justify-center">{n}</span>
                  ))}
                </span>
                It only takes 3 steps — let's go!
              </p>
            </div>

            {/* Step 1: Region Selection */}
            <div className="bg-white rounded-2xl shadow-sm border-2 border-stone-400 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex flex-col items-center justify-center shrink-0 bg-[#722F37] rounded-lg px-2.5 py-1.5 min-w-[38px]">
                  <span className="text-[8px] font-bold text-[#C9A227] uppercase tracking-widest leading-none">Step</span>
                  <span className="text-xl font-bold text-white leading-tight">1</span>
                </div>
                <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
                  Choose Your <span className="text-[#722F37] bg-[#722F37]/10 px-1.5 py-0.5 rounded">Wine Region</span>
                </h3>
              </div>
              <RegionSelector
                value={selectedRegion}
                onChange={setSelectedRegion}
              />
              <p className="text-sm text-stone-600 mt-2 ml-14">Tap to select wine regions</p>
            </div>

            {/* Steps 2 & 3 — ferry islands skip routing, mainland regions use normal route builder */}
            {FERRY_ISLAND_REGIONS.has(selectedRegion) ? (
              <div className="bg-white rounded-2xl shadow-sm border-2 border-stone-400 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex flex-col items-center justify-center shrink-0 bg-[#722F37] rounded-lg px-2.5 py-1.5 min-w-[38px]">
                    <span className="text-[8px] font-bold text-[#C9A227] uppercase tracking-widest leading-none">Step</span>
                    <span className="text-xl font-bold text-white leading-tight">2</span>
                  </div>
                  <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
                    View <span className="text-[#722F37] bg-[#722F37]/10 px-1.5 py-0.5 rounded">Island Wineries</span>
                  </h3>
                </div>
                <p className="text-sm text-stone-500 mb-4 ml-12">
                  This island is reached by BC Ferries. Tap below to see winery details and ferry crossing information.
                </p>
                <button
                  onClick={() => setView(VIEW_STATES.FERRY)}
                  className="w-full h-12 bg-[#722F37] hover:bg-[#5a252c] text-white font-semibold rounded-xl transition-colors"
                >
                  View Island Wineries &amp; Ferry Info
                </button>
              </div>
            ) : (
              <>
                {/* Step 2: Wine Preferences */}
                <div className="bg-white rounded-2xl shadow-sm border-2 border-stone-400 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex flex-col items-center justify-center shrink-0 bg-[#722F37] rounded-lg px-2.5 py-1.5 min-w-[38px]">
                      <span className="text-[8px] font-bold text-[#C9A227] uppercase tracking-widest leading-none">Step</span>
                      <span className="text-xl font-bold text-white leading-tight">2</span>
                    </div>
                    <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
                      Choose Your <span className="text-[#722F37] bg-[#722F37]/10 px-1.5 py-0.5 rounded">Wine Preferences</span>
                    </h3>
                  </div>
                  <WinePreferencesModal
                    preferences={winePreferences}
                    onChange={setWinePreferences}
                    region={selectedRegion}
                  />
                </div>

                {/* Step 3: Plan Route */}
                <div className="bg-white rounded-2xl shadow-sm border-2 border-stone-400 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex flex-col items-center justify-center shrink-0 bg-[#722F37] rounded-lg px-2.5 py-1.5 min-w-[38px]">
                      <span className="text-[8px] font-bold text-[#C9A227] uppercase tracking-widest leading-none">Step</span>
                      <span className="text-xl font-bold text-white leading-tight">3</span>
                    </div>
                    <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
                      Plan Your <span className="text-[#722F37] bg-[#722F37]/10 px-1.5 py-0.5 rounded">Wine Route</span>
                    </h3>
                  </div>
                  <RouteBuilder
                    onBuildRoute={handleBuildRoute}
                    isLoading={isLoading}
                    selectedRegion={selectedRegion}
                    error={error}
                    onClearHistory={() => { clearVisitedWineries(); setError(null); }}
                  />
                </div>
              </>
            )}

            {/* OR Divider */}
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-stone-300" />
              <span className="text-xs font-bold text-white bg-[#722F37] px-3 py-1 rounded-full tracking-widest">OR</span>
              <div className="flex-1 h-px bg-stone-300" />
            </div>

            {/* Find a Specific Winery */}
            <div className="bg-white rounded-2xl shadow-sm border-2 border-stone-400 p-4">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide">
                  Find a <span className="text-[#722F37] bg-[#722F37]/10 px-1.5 py-0.5 rounded">Specific Winery</span> to Visit
                </h3>
              </div>
              <WinerySearch onSelectWinery={handleSelectWinery} />
            </div>
          </div>
        )}

        {/* Ferry Island View */}
        {view === VIEW_STATES.FERRY && (
          <FerryIslandView
            region={selectedRegion}
            onBack={() => setView(VIEW_STATES.HOME)}
          />
        )}

        {/* Route View */}
        {view === VIEW_STATES.ROUTE && currentRoute && (
          <div>
            {isOffline && (
              <div className="mb-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                <span className="text-base">📍</span>
                <span>No internet — your route is saved. Navigate will work when signal returns.</span>
              </div>
            )}
            {isCachedRoute && (
              <div className="mb-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-amber-700 text-sm">
                <span className="text-base">📶</span>
                <span>Showing your saved route from your last visit. Tap <strong>New Route</strong> to build a fresh one.</span>
              </div>
            )}
            <RouteDisplay
              route={currentRoute}
              onReset={handleReset}
              onSelectWinery={handleSelectWinery}
              userLocation={userLocation}
            />
          </div>
        )}
      </main>

      {/* Winery Detail Modal */}
      <AnimatePresence>
        {selectedWinery && (
          <WineryDetail
            winery={selectedWinery}
            onClose={handleCloseDetail}
          />
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="max-w-lg mx-auto px-4 py-8 text-center">
        <p className="text-xs text-stone-600">
          Please drink responsibly. Designate a driver.{' '}
          <button
            onClick={() => setShowDisclaimer(true)}
            className="underline hover:text-stone-600 transition-colors"
          >
            Legal
          </button>
        </p>
      </footer>

      {/* Help Overlay */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              className="w-full max-w-lg bg-white rounded-t-2xl p-6 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-bold text-stone-800">How to Use the Wine Route Planner</h2>
                <button onClick={() => setShowHelp(false)} className="text-stone-400 hover:text-stone-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-5 text-sm text-stone-600 overflow-y-auto max-h-[65vh]">
                <div>
                  <p className="font-semibold text-stone-800 mb-1">Step 1 — Choose Your Wine Region</p>
                  <p>Tap the map to choose a wine region across Vancouver Island — from the Comox Valley and Denman & Hornby Islands in the north, to the Cowichan Valley, Nanaimo, the Saanich Peninsula, and the Southern Gulf Islands.</p>
                </div>
                <div>
                  <p className="font-semibold text-stone-800 mb-1">Step 2 — Choose Your Wine Preferences <span className="font-normal text-stone-400">(Optional)</span></p>
                  <p>Tap the preferences button to select the wines you love — reds, whites, rosé, sparkling, or cider. If you're open to anything, skip this step and we'll include all wineries.</p>
                </div>
                <div>
                  <p className="font-semibold text-stone-800 mb-1">Step 3 — Plan Your Wine Route</p>
                  <p className="mb-2">Choose how much time you have:</p>
                  <ul className="space-y-1 ml-3">
                    <li><span className="font-medium text-[#722F37]">2 Hours</span> — 2 stops, a quick tasting tour</li>
                    <li><span className="font-medium text-[#722F37]">Half Day</span> — 4 stops, a leisurely exploration</li>
                    <li><span className="font-medium text-[#722F37]">Full Day</span> — 6 stops, the complete adventure</li>
                  </ul>
                  <p className="mt-2">For the most efficient driving order, tap <span className="font-medium text-stone-700">Use My Location</span> — this also enables turn-by-turn directions once your route is ready. Then tap <span className="font-medium text-stone-700">Build My Wine Route</span>.</p>
                </div>
                <div>
                  <p className="font-semibold text-stone-800 mb-1">Exploring Your Route</p>
                  <p>Tap any winery on your route to see details — hours, tasting fees, website link, and directions. Use the <span className="font-medium text-stone-700">Share</span> button to send your route to friends joining you for the day.</p>
                </div>
                <div className="pt-1 border-t border-stone-100">
                  <p className="text-xs text-stone-400">Questions or feedback? Email us at <span className="text-[#722F37] no-underline select-none">info@okanaganwineroute.ca</span></p>
                </div>
                <div className="pt-3 border-t border-stone-100">
                  <p className="text-xs text-stone-500 mb-2">Visited wineries are remembered so they don't appear in future routes. You can reset this at any time.</p>
                  <button
                    onClick={() => {
                      if (window.confirm('Clear your visited wineries history? Your next route will include all wineries again.')) {
                        localStorage.removeItem('cowichan_visited_wineries');
                        setShowHelp(false);
                      }
                    }}
                    className="w-full py-2 border border-stone-300 text-stone-600 text-sm rounded-xl hover:bg-stone-50 transition-colors"
                  >
                    Clear Visited Wineries
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className="mt-5 w-full py-2.5 bg-[#722F37] text-white text-sm font-semibold rounded-xl hover:bg-[#5a252c] transition-colors"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer Modal */}
      <AnimatePresence>
        {showDisclaimer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center"
            onClick={() => setShowDisclaimer(false)}
          >
            <motion.div
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              className="w-full max-w-lg bg-white rounded-t-2xl p-6 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-stone-800">Legal Disclaimer</h2>
                <button onClick={() => setShowDisclaimer(false)} className="text-stone-400 hover:text-stone-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3 text-sm text-stone-600 overflow-y-auto max-h-[60vh]">
                <p>
                  This website is intended only for individuals who are of legal drinking age in British Columbia,
                  which is 19 years or older. By accessing or using this service, you confirm that you are legally
                  permitted to view alcohol-related information and to purchase and consume alcohol where you are located.
                </p>
                <p>
                  The Cowichan Wine Route Planner is a free, non-commercial project provided for informational and
                  recreational purposes only. While we try to keep information current, we do not guarantee the accuracy,
                  completeness, or timeliness of any winery information, including hours of operation, tasting
                  availability, pricing, events, products, locations, or services. Winery details may change without
                  notice, and users should confirm all details directly with the winery before visiting.
                </p>
                <p>
                  This service includes links to external winery websites for your convenience. We are not responsible
                  for the content, accuracy, availability, or privacy practices of any third-party websites. Visiting
                  those links is entirely at your own discretion.
                </p>
                <p>
                  This service collects anonymous usage statistics, such as feature interactions and visit frequency,
                  for the purpose of improving the application. No personally identifiable information is collected
                  or stored.
                </p>
                <p>
                  Unless expressly stated otherwise, this service is not affiliated with, endorsed by, or sponsored
                  by any winery, business, or organization listed on the site.
                </p>
                <p>
                  Always drink responsibly. Never drink and drive. Users are solely responsible for arranging safe
                  transportation, including a designated driver, taxi, rideshare, or other appropriate travel option
                  before participating in any wine tasting activities.
                </p>
                <p>
                  Use of this website and reliance on any information provided is at your own risk. To the fullest
                  extent permitted by applicable law, the creators and operators of this service are not liable for
                  any loss, injury, damage, delay, inconvenience, or other claim arising from or related to use of
                  this service or visits to any listed winery or destination.
                </p>
                <p>
                  This disclaimer is governed by the laws of British Columbia and Canada.
                </p>
              </div>
              <button
                onClick={() => setShowDisclaimer(false)}
                className="mt-5 w-full py-2.5 bg-[#722F37] text-white text-sm font-semibold rounded-xl hover:bg-[#5a252c] transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
